// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/views/jobs/searches/SearchCard.js
//
// Carte d'une recherche (module « Recherches & entraide pro »), partagée par « Recherches du
// réseau » (lecture) et « Mes recherches » (gestion). Aucune règle métier ici : les actions
// affichées sont celles que le serveur autorise (`canEdit`, `canRenew`…), lues par availableActions.

import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CCard, CBadge, CButton, CSpinner } from '@coreui/react'
import Avatar from '../../../components/shared/Avatar'
import { formatDateI18n } from '../../../utils/i18nFormatters'
import { getContractTypeLabel } from '../../../utils/contractTypes'
import { getJobFamilyLabel } from '../../../utils/jobFamilies'
import { getSectorLabel } from '../../../utils/sectors'
import { canonicalCountryName } from '../../../utils/countryCode'
import {
  statusColor,
  isExpiringSoon,
  availableActions,
  isDeletedAuthor,
  authorName,
  safeHttpsUrl,
  canShowMatches,
} from '../../../utils/memberSearches'
import { responseStatusColor } from '../../../utils/memberSearchResponses'
import './searches.scss'

const SCOPE_KEYS = {
  network: 'card.scope_network',
  campus: 'card.scope_campus',
  entity: 'card.scope_entity',
}

const SearchCard = ({
  search,
  mode = 'network',
  busy = false,
  onEdit,
  onRenew,
  onReactivate,
  onClose,
  onMatches,
  onResponses,
  pendingCount,
  helperState,
  onHelp,
  onOpenConversation,
}) => {
  const { t, i18n } = useTranslation()
  const mine = mode === 'mine'

  // Réseau : seule la modération d'un admin sur l'annonce d'un AUTRE membre est proposée.
  // Mes recherches : toutes les actions autorisées par le serveur.
  const allowed = availableActions(search)
  const actions = mine ? allowed : allowed.filter((a) => a === 'close' && !search.isMine)

  const chips = []
  ;(search.contractTypes || []).forEach((c) =>
    chips.push({ key: `c-${c}`, label: getContractTypeLabel(c, t) }),
  )
  if (search.jobFamily) chips.push({ key: 'jf', label: getJobFamilyLabel(search.jobFamily, t) })
  if (search.sector) chips.push({ key: 'sec', label: getSectorLabel(search.sector, t) })
  const place = [search.city, search.country ? canonicalCountryName(search.country) : '']
    .filter(Boolean)
    .join(', ')
  if (place) chips.push({ key: 'place', label: place })
  if (search.remoteOk) chips.push({ key: 'remote', label: t('searches:card.remote') })

  const cvHref = safeHttpsUrl(search.cvUrl)
  const deleted = isDeletedAuthor(search)
  const name = authorName(search)
  const author = search.author

  // « Je peux aider » : côté demandeur, l'accès aux propositions reçues (demandes de mise en relation seulement) ;
  // côté aidant, le bouton ou le statut de MA proposition. Aucune règle ici : l'état est dérivé par helperStateFor.
  const showResponses = mine && Boolean(onResponses) && search.kind === 'intro_request'
  const helperKind = !mine && helperState ? helperState.kind : 'none'
  const showHelper = helperKind !== 'none'

  return (
    <CCard className="aw-elev-1 ms-card">
      <div className="ms-card-head">
        <CBadge color="primary" className="ms-badge-kind">
          {t(`searches:kind.${search.kind}.short`)}
        </CBadge>
        {mine && (
          <CBadge color={statusColor(search.status)}>
            {t(`searches:status.${search.status}`)}
          </CBadge>
        )}
        {isExpiringSoon(search) && (
          <CBadge color="warning">
            {t('searches:card.expires_in', { count: search.daysLeft })}
          </CBadge>
        )}
        {mine && search.scopeKind && (
          <span className="ms-scope text-medium-emphasis small">
            {t(`searches:${SCOPE_KEYS[search.scopeKind] || SCOPE_KEYS.network}`)}
          </span>
        )}
      </div>

      <h5 className="ms-card-title">{search.title}</h5>
      <p className="ms-card-desc">{search.description}</p>

      {chips.length > 0 && (
        <div className="ms-chips">
          {chips.map((c) => (
            <span key={c.key} className="ms-chip">
              {c.label}
            </span>
          ))}
        </div>
      )}

      <ul className="ms-facts">
        {search.targetCompanyName && (
          <li>{t('searches:card.target_company', { name: search.targetCompanyName })}</li>
        )}
        {search.availableFrom && (
          <li>
            {t('searches:card.available_from', {
              date: formatDateI18n(search.availableFrom, 'P', i18n.language),
            })}
          </li>
        )}
        {search.status === 'active' && !isExpiringSoon(search) && mine && (
          <li>{t('searches:card.expires_in', { count: search.daysLeft })}</li>
        )}
      </ul>

      <div className="ms-card-foot">
        {!mine && author && (
          <div className="ms-author">
            {deleted ? (
              <span className="text-medium-emphasis small">{t('searches:author.deleted')}</span>
            ) : (
              <Link to={`/members/${author.id}`} className="ms-author-link">
                <Avatar user={author} size={28} />
                <span className="small">{t('searches:card.by', { name })}</span>
              </Link>
            )}
          </div>
        )}
        {cvHref && (
          <a href={cvHref} target="_blank" rel="noopener noreferrer" className="small">
            {t('searches:card.cv')}
          </a>
        )}

        {(actions.length > 0 ||
          showResponses ||
          showHelper ||
          (mine && onMatches && canShowMatches(search))) && (
          <div className="ms-actions">
            {busy && <CSpinner size="sm" />}
            {showResponses && (
              <CButton
                size="sm"
                color="primary"
                disabled={busy}
                onClick={() => onResponses(search)}
              >
                {t('searches:responses.author_button')}
                {pendingCount > 0 && (
                  <CBadge color="light" textColor="dark" className="ms-2">
                    {t('searches:responses.pending_badge', { count: pendingCount })}
                  </CBadge>
                )}
              </CButton>
            )}
            {helperKind === 'can_help' && (
              <CButton size="sm" color="primary" disabled={busy} onClick={() => onHelp(search)}>
                {t(
                  helperState.reproposal
                    ? 'searches:responses.repropose'
                    : 'searches:responses.help',
                )}
              </CButton>
            )}
            {showHelper && helperKind !== 'can_help' && (
              <CBadge color={responseStatusColor(helperKind)}>
                {t(`searches:responses.status.${helperKind}`)}
              </CBadge>
            )}
            {helperState && helperState.conversationId && onOpenConversation && (
              <CButton
                size="sm"
                color="primary"
                variant="outline"
                onClick={() => onOpenConversation(helperState.conversationId)}
              >
                {t('searches:responses.open_conversation')}
              </CButton>
            )}
            {mine && onMatches && canShowMatches(search) && (
              <CButton size="sm" color="primary" variant="outline" disabled={busy} onClick={() => onMatches(search)}>
                {t('searches:matching.button')}
              </CButton>
            )}
            {actions.includes('edit') && (
              <CButton size="sm" color="light" disabled={busy} onClick={() => onEdit(search)}>
                {t('searches:actions.edit')}
              </CButton>
            )}
            {actions.includes('renew') && (
              <CButton size="sm" color="light" disabled={busy} onClick={() => onRenew(search)}>
                {t('searches:actions.renew')}
              </CButton>
            )}
            {actions.includes('reactivate') && (
              <CButton
                size="sm"
                color="primary"
                variant="outline"
                disabled={busy}
                onClick={() => onReactivate(search)}
              >
                {t('searches:actions.reactivate')}
              </CButton>
            )}
            {actions.includes('close') && (
              <CButton
                size="sm"
                color="danger"
                variant="ghost"
                disabled={busy}
                onClick={() => onClose(search)}
              >
                {t('searches:actions.close')}
              </CButton>
            )}
          </div>
        )}
      </div>
    </CCard>
  )
}

export default SearchCard
